import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Rnd } from 'react-rnd';
import {
	Button,
	Typography,
	Grid,
	TextField,
	FormControlLabel,
	FormControl,
	InputLabel,
	Select,
	FormHelperText,
	MenuItem,
	IconButton,
	ButtonBase,
	Checkbox,
	Backdrop,
	CircularProgress,
} from '@mui/material';
import BaseLayout from '../BaseLayout';
import routes from '../../constants/Routes';
import history from '../../router/history';
import * as utils from '../../util/bellplus-utils';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { ToastsContainer, ToastsStore, ToastsContainerPosition } from 'react-toasts';
import { convertDOMToBlobFilePromise } from '../../util/imageutils';
import AddVideoButtonIcon from '../../images/template/movie-icon-1.png';
import AddImageButtonIcon from '../../images/template/image-icon-1.png';
import AddMultipleImageButtonIcon from '../../images/template/Multi-image-icon-1.png';
import AddDateTimeIcon from '../../images/template/date-time-icon-1.png';
import AddWeatherIcon from '../../images/template/weather-icon-1.png';
import VideoSvgThumbnail from '../../images/template/Movie-Icon-75x75.svg';
import ImageSvgThumbnail from '../../images/template/Image-icon-75x75.svg';
import DeleteIcon from '@mui/icons-material/Delete';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import FlipToBackIcon from '@mui/icons-material/FlipToBack';
import { validateSchema } from './TemplateValidation';
import TemplateErrorBoundry from './TemplateErrorBoundry';
import { getDeviceTypes } from '../../services/devices.service';
import {
	createTemplate,
	updateTemplate,
	getTemplateById,
	uploadAsset,
} from '../../services/template.service';
//import { Template, BOX_LAYOUT_TYPE, BoxLayoutProperties } from '../../types/template';
import {
	Template,
	BOX_LAYOUT_TYPE,
	BoxLayoutProperties,
} from '@horasys/bellplus/common/types';
import { DeviceType } from '@horasys/bellplus/common/types';
import loadash from 'lodash';
import AlertBox from 'views/Common/AlertBox';
import theme from 'theme';
import { CssTextField } from 'views/Common/CssTextField';
import { CssSelect } from 'views/Common/CssSelect';

const PREFIX = 'TemplateForm';

const classes = {
    gridElement: `${PREFIX}-gridElement`,
    title: `${PREFIX}-title`,
    divStyle: `${PREFIX}-divStyle`,
    boxProperties: `${PREFIX}-boxProperties`,
    boxPropertiesParent: `${PREFIX}-boxPropertiesParent`,
    secondaryAppbar: `${PREFIX}-secondaryAppbar`,
    formElement: `${PREFIX}-formElement`,
    submitContainer: `${PREFIX}-submitContainer`,
    backdrop: `${PREFIX}-backdrop`,
    addVideoButton: `${PREFIX}-addVideoButton`,
    addImageButton: `${PREFIX}-addImageButton`,
    addMultipleImageButton: `${PREFIX}-addMultipleImageButton`,
    addDateTimeButton: `${PREFIX}-addDateTimeButton`,
    addWeatherButton: `${PREFIX}-addWeatherButton`,
    btnTextSize: `${PREFIX}-btnTextSize`,
    addContainerGrid: `${PREFIX}-addContainerGrid`
};

const Root = styled('div')((
    {
        theme: Theme
    }
) => ({
    [`& .${classes.gridElement}`]: {
        minHeight: theme.spacing(100),
        backgroundColor: '#ffffff',
    },

    [`& .${classes.title}`]: {
        marginTop: theme.spacing(2),
        minWidth: '100%',
        paddingTop: theme.spacing(2),
    },

    [`& .${classes.divStyle}`]: {
        position: 'relative',
        margin: theme.spacing(3),
        border: '1px solid',
    },

    [`& .${classes.boxProperties}`]: {
        marginTop: theme.spacing(1),
    },

    [`& .${classes.boxPropertiesParent}`]: {
        padding: theme.spacing(1),
    },

    [`& .${classes.secondaryAppbar}`]: {
        backgroundColor: '#fafaff',
    },

    [`& .${classes.formElement}`]: {
        minWidth: '100%',
        marginTop: theme.spacing(2),
    },

    [`& .${classes.submitContainer}`]: {
        marginTop: theme.spacing(4),
    },

    [`& .${classes.backdrop}`]: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },

    [`& .${classes.addVideoButton}`]: {
        width: '75px',
        height: '75px',
        color: 'blue',
        backgroundImage: `url(${AddVideoButtonIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '90%',
        border: '1px solid #36c6d6',
    },

    [`& .${classes.addImageButton}`]: {
        width: '75px',
        height: '75px',
        color: 'blue',
        backgroundImage: `url(${AddImageButtonIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '90%',
        border: '1px solid #36c6d6',
    },

    [`& .${classes.addMultipleImageButton}`]: {
        width: '75px',
        height: '75px',
        color: 'blue',
        backgroundImage: `url(${AddMultipleImageButtonIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '90%',
        border: '1px solid #36c6d6',
    },

    [`& .${classes.addDateTimeButton}`]: {
        width: '75px',
        height: '75px',
        color: 'blue',
        backgroundImage: `url(${AddDateTimeIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '90%',
        border: '1px solid #36c6d6',
    },

    [`& .${classes.addWeatherButton}`]: {
        width: '75px',
        height: '75px',
        color: 'blue',
        backgroundImage: `url(${AddWeatherIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '90%',
        border: '1px solid #36c6d6',
    },

    [`& .${classes.btnTextSize}`]: {
        fontSize: '0.8rem',
        // textAlign: 'center',
    },

    [`& .${classes.addContainerGrid}`]: {
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(3),
    }
}));

const dateValues = ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];

const TemplateForm: React.FC = () => {
	const mode = utils.getData('mode');
	const divRef = useRef(null);
	const [progress, setProgress] = React.useState(false);
	const [deviceTypes, setDeviceTypes] = useState<Array<DeviceType>>([]);
	const emptyDeviceType: DeviceType = {
		id: 0,
		deviceType: '',
		qrScannerEnabled: false,
		height: 0,
		width: 0,
	};
	const [templateErrorPopup, setTemplateErrorPopup] = useState(false);
	const [alertError, setAlertError] = useState({ message: '', open: false });
	// Get DeviceType List
	useEffect(() => {
		const getDeviceTypeList = async () => {
			const deviceListObjectList = await getDeviceTypes();
			const parsedDeviceList = deviceListObjectList.map((deviceType: any) => {
				return {
					id: deviceType.id,
					width: parseInt(deviceType.width),
					height: parseInt(deviceType.height),
					deviceType: deviceType.deviceType,
					qrScannerEnabled: deviceType.qrScannerEnabled,
				};
			});
			setDeviceTypes(parsedDeviceList);
		};

		getDeviceTypeList();
	}, []);

	useEffect(() => {
		const getDetails = async () => {
			if (mode === 'EDIT' || mode === 'VIEW') {
				const valuesTextSize = [];
				for (let i = 0; i <= 60; i++) {
					valuesTextSize.push(i);
				}
				setConstantTextSize(valuesTextSize);
				const templateId = utils.getData('templateId');
				try {
					const template: Template = await getTemplateById(templateId);
					const updatedFactor = getMultiplicationFactor(template);
					console.warn(' i am here called', updatedFactor);
					console.warn('template', template);
					setMultiplicationFactor(updatedFactor);
					const updatedTemplate = { ...template };
					const updatedAssets = template.assets.map(
						(asset: BoxLayoutProperties) => {
							const updatedAsset = { ...asset };
							updatedAsset.width = asset.width / updatedFactor;
							updatedAsset.height = asset.height / updatedFactor;
							updatedAsset.left = asset.left / updatedFactor;
							updatedAsset.top = asset.top / updatedFactor;
							return updatedAsset;
						},
					);
					console.warn(updatedAssets);
					updatedTemplate.assets = updatedAssets;
					setTemplate(template);
				} catch (error) {
					const alert = {
						message: 'Unable to fetch Template details',
						open: true,
					};
					setAlertError(alert);
				}

				if (mode === 'VIEW') {
					setViewMode(true);
				}
			}
		};
		getDetails();
	}, [mode]);

	const defaultAsset: BoxLayoutProperties = {
		left: 0,
		top: 0,
		width: 100,
		height: 100,
		type: 'video',
		zIndex: 1,
		isPrimary: true,
		name: 'default',
		maxSize: 'test',
		status: '',
		templateId: 0,
		id: 0,
		action: '',
		innerText: '',
		fontSize: 15,
		backgroundColor: '',
		fontColor: '',
		locationText: true,
		weatherText: true,
		boxShadow: '',
		isActive: false,
		overflow: '',
		dateStyle: 'DD/MM/YYYY',
		multipleImageCount: 1,
	};
	const emptyTemplate: Template = {
		name: '',
		templateUrl: '',
		duration: 0,
		thumbnailAssetId: 2,
		assets: [],
		id: 0,
		deviceType: emptyDeviceType,
		standard: false,
	};

	const [viewMode, setViewMode] = useState<boolean>(false);
	const [template, setTemplate] = useState<Template>(emptyTemplate);
	const [boxLayoutProperties, setBoxLayoutProperties] = useState<BoxLayoutProperties>(
		defaultAsset,
	);
	const [selectedIndex, setSelectedIndex] = useState<number>(-1);
	const [dateSelect, setDateSelect] = useState(false);
	const [weatherSelect, setWeatherSelect] = useState(false);
	const [multipleImageSelect, setMultipleImageSelect] = useState(false);

	const [currentDate] = useState(() => {
		const initialState = new Date()
			.toISOString()
			.slice(0, 10)
			.split(`-`)
			.reverse()
			.join(`/`);
		return initialState;
	});
	const [constantTextSize, setConstantTextSize] = useState<any>([]);

	const [error, setError] = useState({ name: '', assets: '' });

	const getStyle = (widget: BoxLayoutProperties) => {
		let thumbnail;

		switch (widget.type) {
			case 'video':
				thumbnail = VideoSvgThumbnail;
				break;
			case 'image':
				thumbnail = ImageSvgThumbnail;
				break;
			case 'multipleImages':
				thumbnail = ImageSvgThumbnail;
				break;
			case 'weather':
				return {
					display: 'absolute',
					alignItems: 'center',
					justifyContent: 'center',
					border: 'solid 1px #ddd',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
					backgroundSize: '50%',
					fontSize: widget.fontSize,
					backgroundColor: widget.backgroundColor,
					color: widget.fontColor,
					weatherText: widget.weatherText,
					boxShadow: widget.isActive ? widget.boxShadow : '',
					overflow: 'hidden',
				};
			case 'datetime':
				return {
					display: 'absolute',
					alignItems: 'center',
					justifyContent: 'center',
					border: 'solid 1px #ddd',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
					backgroundSize: '50%',
					backgroundColor: widget.backgroundColor,
					fontSize: widget.fontSize,
					color: widget.fontColor,
					padding: '2px',
					boxShadow: widget.isActive ? widget.boxShadow : '',
					overflow: 'hidden',
					dateStyle: widget.dateStyle,
					innerText: widget.innerText,
				};
			default:
				break;
		}
		return {
			display: 'absolute',
			alignItems: 'center',
			justifyContent: 'center',
			border: 'solid 1px #ddd',
			backgroundImage: `url(${thumbnail})`,
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			backgroundSize: '50%',
			backgroundColor: '#36c6d6',
			boxShadow: widget.isActive ? widget.boxShadow : '',
			// boxShadow: '0px 3px 8px red',
		};
	};
	const getMultiplicationFactor = (template: Template) => {
		try {
			if (
				template &&
				template.deviceType &&
				template.deviceType.deviceType &&
				template.deviceType.deviceType.toLowerCase() === 'kiosk'
			) {
				return 6;
			} else {
				return 2.5;
			}
		} catch (error) {
			return 1;
		}
	};
	const [multiplicationFactor, setMultiplicationFactor] = useState(2);
	const resolutionStyle = {
		width: template.deviceType.width / multiplicationFactor,
		height: template.deviceType.height / multiplicationFactor,
	};


	const showDateSelector = () => {
		setDateSelect(true);
	};

	const showWeatherSelect = () => {
		setWeatherSelect(true);
	};

	const showMultipleImageSelect = () => {
		setMultipleImageSelect(true);
	};

	const textSizeValues = () => {
		const values = [];
		for (let i = 0; i <= 60; i++) {
			values.push(i);
		}
		setConstantTextSize(values);
	};

	const addAssetHandler = (assetType: BOX_LAYOUT_TYPE) => {
		textSizeValues();
		const asset: BoxLayoutProperties = {
			left: 0,
			top: 0,
			width: 100,
			height: 100,
			type: assetType,
			zIndex: 1,
			isPrimary: false,
			name: 'default',
			maxSize: 'test',
			templateId: 0,
			status: '',
			id: 0,
			action: 'CREATE',
			innerText: '',
			fontSize: 15,
			backgroundColor: '',
			fontColor: '',
			locationText: true,
			weatherText: true,
			boxShadow: '',
			isActive: false,
			overflow: '',
			dateStyle: 'DD/MM/YYYY',
			multipleImageCount: 1,
		};
		if (assetType === 'datetime') {
			showDateSelector();
			setWeatherSelect(false);
			asset.innerText = currentDate;
			asset.height = 33.5;
			asset.width = 90;
			asset.fontSize = 15;
			asset.backgroundColor = '#FFFFFF';
			asset.fontColor = '#000000';
			asset.dateStyle = 'DD/MM/YYYY';
		} else if (assetType === 'weather') {
			showWeatherSelect();
			setDateSelect(false);
			asset.height = 30;
			asset.width = 170;
			asset.backgroundColor = '#FFFFFF';
			asset.fontColor = '#000000';
			asset.weatherText = true;
			asset.locationText = true;
		} else if (assetType === 'multipleImages') {
			showMultipleImageSelect();
			setDateSelect(false);
			setWeatherSelect(false);
		} else {
			setDateSelect(false);
			setWeatherSelect(false);
			setMultipleImageSelect(false);
		}
		const updatedTemplate = { ...template };

		if (!checkForIsPrimary()) {
			if (
				asset.type === 'image' ||
				asset.type === 'video' ||
				asset.type === 'multipleImages'
			) {
				asset.isPrimary = true;
			}
		}

		updatedTemplate.assets.push(asset);
		setTemplate(updatedTemplate);
	};

	const checkForIsPrimary = () => {
		let isPresent = false;

		template.assets.forEach((asset: BoxLayoutProperties) => {
			if (asset.isPrimary) {
				isPresent = true;
			}
		});

		return isPresent;
	};

	const handleBoxLayoutProperties = (event: any) => {
		const selectedWidget = { ...template.assets[selectedIndex] };
		switch (event.target.name) {
			case 'top':
				selectedWidget.top = event.target.value / multiplicationFactor;
				break;

			case 'left':
				selectedWidget.left = event.target.value / multiplicationFactor;
				break;

			case 'width':
				selectedWidget.width = event.target.value / multiplicationFactor;
				break;

			case 'height':
				selectedWidget.height = event.target.value / multiplicationFactor;
				break;

			case 'isPrimary':
				selectedWidget.isPrimary = event.target.checked;
				break;

			case 'dateValue':
				{
					selectedWidget.dateStyle = event.target.value;
					const date = new Date();
					let newDate = '';
					switch (event.target.value) {
						case 'DD-MM-YYYY':
							newDate = date
								.toJSON()
								.slice(0, 10)
								.split('-')
								.reverse()
								.join('-');
							break;
						case 'DD/MM/YYYY':
							newDate = date
								.toJSON()
								.slice(0, 10)
								.split('-')
								.reverse()
								.join('/');
							break;
						case 'YYYY-MM-DD':
							newDate = date.toJSON().slice(0, 10).split('-').join('-');
							break;
						default:
							newDate = date
								.toJSON()
								.slice(0, 10)
								.split('/')
								.reverse()
								.join('/');
							break;
					}
					selectedWidget.innerText = newDate;
				}

				break;

			case 'innerText':
				selectedWidget.innerText = event.target.value;
				break;

			case 'fontSize':
				selectedWidget.fontSize = event.target.value;
				break;

			case 'backgroundColor':
				selectedWidget.backgroundColor = event.target.value;
				break;

			case 'fontColor':
				selectedWidget.fontColor = event.target.value;
				break;

			case 'weatherText':
				selectedWidget.weatherText = event.target.checked;
				break;

			case 'locationText':
				selectedWidget.locationText = event.target.checked;
				break;
			case 'multipleImages':
				selectedWidget.multipleImageCount = event.target.value;
				break;

			default: {
				const alert = { message: 'Something went wrong!', open: true };
				setAlertError(alert);
			}
		}
		updateTemplateWithSelectedAsset(selectedWidget, selectedIndex);
	};

	const handleDragStart = (selectedIndex: any) => {
		if (viewMode) {
			return;
		}
		if (template.assets[selectedIndex].type === 'datetime') {
			setDateSelect(true);
		} else {
			setDateSelect(false);
		}
		if (template.assets[selectedIndex].type === 'weather') {
			setWeatherSelect(true);
		} else {
			setWeatherSelect(false);
		}
		if (template.assets[selectedIndex].type === 'multipleImages') {
			setMultipleImageSelect(true);
		} else {
			setMultipleImageSelect(false);
		}

		if (template.assets[selectedIndex]) {
			template.assets[selectedIndex].boxShadow = 'red 0px 0px 2.5px 1px';
		}

		setSelectedIndex(selectedIndex);
		const selectedWidget = { ...template.assets[selectedIndex] };
		updateTemplateWithSelectedAsset(selectedWidget, selectedIndex);
	};

	const handleDrag = (event: any, dragData: any, selectedIndex: any) => {
		if (viewMode) {
			return;
		}

		setSelectedIndex(selectedIndex);
		updateActiveState(selectedIndex);
		const selectedWidget = { ...template.assets[selectedIndex] };
		selectedWidget.left = dragData.x;
		selectedWidget.top = dragData.y;
		updateTemplateWithSelectedAsset(selectedWidget, selectedIndex);
	};

	const updateActiveState = (selectedIndex: number) => {
		const updatedTemplate: Template = { ...template };
		updatedTemplate.assets.forEach(
			(asset: BoxLayoutProperties) => (asset.isActive = false),
		);
		updatedTemplate.assets[selectedIndex].isActive = true;
		setTemplate(updatedTemplate);
	};

	const handleResize = (ref: any, position: any, selectedIndex: any) => {
		if (viewMode) {
			return;
		}

		setSelectedIndex(selectedIndex);
		const selectedWidget = { ...template.assets[selectedIndex] };
		selectedWidget.width = ref.offsetWidth;
		selectedWidget.height = ref.offsetHeight;
		selectedWidget.top = position.y;
		selectedWidget.left = position.x;
		updateTemplateWithSelectedAsset(selectedWidget, selectedIndex);
	};

	const updateTemplateWithSelectedAsset = (
		selectedAsset: BoxLayoutProperties,
		selectedAssetIndex: number,
	) => {
		const newWidgets = template.assets.map(
			(widget: BoxLayoutProperties, index: number) => {
				if (selectedAssetIndex !== index) {
					return widget;
				} else {
					return selectedAsset;
				}
			},
		);

		const updatedTemplate = { ...template };
		updatedTemplate.assets = [...newWidgets];
		setTemplate(updatedTemplate);
		setBoxLayoutProperties(selectedAsset);
	};

	const handleSubmit = async () => {
		try {
			await validateSchema(template);

			if (mode === 'EDIT') {
				const updatedTemplate: Template = await updateTemplateState();
				const resp: any = await updateTemplate(updatedTemplate, template.id);

				if (
					resp !== undefined &&
					resp.data !== undefined &&
					resp.data.errors !== undefined &&
					resp.data.errors.length === 0
				) {
					ToastsStore.success(
						'Template updated successfully',
						8000,
						'toast-styles',
					);
					setTimeout(() => {
						history.push(routes.TEMPLATE_LIST.path);
					}, 2000);
				} else {
					ToastsStore.error('Template updation failed', 8000);
				}
			} else {
				try {
					setProgress(true);
					const createdTemplate: Template = await updateTemplateState();
					const resp: any = await createTemplate(createdTemplate);

					if (
						resp !== undefined &&
						resp.data !== undefined &&
						resp.data.errors !== undefined &&
						resp.data.errors.length === 0
					) {
						setProgress(false);
						ToastsStore.success(
							'Template created successfully',
							8000,
							'toast-styles',
						);
						setTimeout(() => {
							history.push(routes.TEMPLATE_LIST.path);
						}, 2000);
					} else {
						setProgress(false);
						ToastsStore.error('Template creation failed', 8000);
					}
				} catch (error) {
					setProgress(false);
					ToastsStore.error('Template creation failed', 8000);
				}
			}
		} catch (error:any) {
			setError(error);
			setTemplateErrorPopup(true);
		}
	};

	const _handleSubmit = loadash.debounce(handleSubmit, 1000);

	const updateTemplateState = async (): Promise<Template> => {
		let file: any;
		const updatedTemplate: Template = { ...template };
		try {
			file = await convertDOMToBlobFilePromise(divRef.current);
			const imageResp: any = await uploadAsset(file, 'layout');
			let imageId;
			if (
				imageResp !== undefined &&
				imageResp.data &&
				imageResp.data.errors !== undefined &&
				imageResp.data.errors.length === 0
			) {
				imageId = imageResp.data.data.id;
			} else {
				ToastsStore.error('Template updation failed', 8000);
				//return Promise.reject("Error");
			}

			updatedTemplate.thumbnailAssetId = imageId;
			setTemplate(updatedTemplate);
			return Promise.resolve(updatedTemplate);
		} catch (error) {
			ToastsStore.error('Template updation failed', 8000);
			return Promise.reject(error);
		}
	};

	const handleTemplateNameChange = (event: any) => {
		const templateName: any = event.target.value;
		const updateTemplate: Template = { ...template };
		updateTemplate.name = templateName;
		setTemplate(updateTemplate);
	};

	const handleDeviceTypeChange = (event: any) => {
		const deviceType: any = event.target.value;
		const selDevice: Array<DeviceType> = deviceTypes.filter(
			(device) => device.deviceType === deviceType,
		);
		const updateTemplate: Template = { ...template };
		updateTemplate.deviceType = selDevice[0];
		updateTemplate.deviceType.width = selDevice[0].width;
		updateTemplate.deviceType.height = selDevice[0].height;
		setTemplate(updateTemplate);
		const updatedFactor = getMultiplicationFactor(updateTemplate);
		setMultiplicationFactor(updatedFactor);
	};

	const deleteAssetHandler = (event: any) => {
		const newWidgets = template.assets.map((widget, index) => {
			if (selectedIndex !== index) {
				return widget;
			} else {
				const newWidget: BoxLayoutProperties = template.assets[selectedIndex];
				newWidget.action = 'DELETE';
				return newWidget;
			}
		});
		const updatedWidgets = newWidgets.filter(function (obj) {
			return obj.action !== 'DELETE';
		});
		const updatedTemplate = { ...template };
		if (mode === 'EDIT') {
			updatedTemplate.assets = [...newWidgets];
			setTemplate(updatedTemplate);
			setBoxLayoutProperties(defaultAsset);
		} else if (mode === 'CREATE') {
			updatedTemplate.assets = [...updatedWidgets];
			setTemplate(updatedTemplate);
			setBoxLayoutProperties(defaultAsset);
		}
	};

	const sendBackWard = () => {
		const selectedWidget = { ...template.assets[selectedIndex] };
		selectedWidget.zIndex = selectedWidget.zIndex - 1;
		updateTemplateWithSelectedAsset(selectedWidget, selectedIndex);
	};
	const handleTemplateErrorClose = () => {
		setTemplateErrorPopup(false);
	};
	const sendForward = () => {
		const selectedWidget = { ...template.assets[selectedIndex] };
		selectedWidget.zIndex = selectedWidget.zIndex + 1;
		updateTemplateWithSelectedAsset(selectedWidget, selectedIndex);
	};

	const TemplateErrorDialog = (props: any) => {
		const { shouldOpen, message, handleTemplateErrorClose } = props;
		const [open, setOpen] = React.useState(false);
		const [errorMsg, setErrorMsg] = React.useState<any>([]);
		useEffect(() => {
			setOpen(shouldOpen);
			const messages: any = [];
			Object.keys(message).forEach((key) => {
				messages.push(message[key]);
			});
			setErrorMsg(messages);
		}, [shouldOpen, message]);
		const handleClose = () => {
			setOpen(false);
			handleTemplateErrorClose();
		};

		return (
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description">
				<DialogTitle id="alert-dialog-title">
					Please fill form accordingly.
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						<ul>
							{errorMsg.map((msg: any, index: number) => (
								<li key={index}>{msg}</li>
							))}
						</ul>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary" autoFocus>
						ok
					</Button>
				</DialogActions>
			</Dialog>
		);
	};

	return (
        <Root style={{padding:'20px'}}>
            <Backdrop className={classes.backdrop} open={progress}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <TemplateErrorBoundry>
                <TemplateErrorDialog
                    shouldOpen={templateErrorPopup}
                    message={error}
                    handleTemplateErrorClose={handleTemplateErrorClose}
                />
                <AlertBox
                    message={alertError.message}
                    shouldOpen={alertError.open}
                    type="warning"
                />
                <ToastsContainer
                    className="toast"
                    store={ToastsStore}
                    position={ToastsContainerPosition.TOP_CENTER}
                />
                <BaseLayout elevation={2}>
                    {/* <Typography
                        variant="h3"
                        component="h3"
                        align="center"
                        className={classes.title}>
                        Layout Design
                    </Typography> */}

                    <Grid
                        container
                        xs={12}
                        direction="row"
                        justifyContent="center"
                        alignItems="center">
                        <Grid item container xs={12} justifyContent="center">
                            <Grid item container xs={4} direction="column">
                                <CssTextField
                                    label="Template Name"
                                    name="templatename"
                                    variant="outlined"
                                    required
                                    autoComplete="off"
                                    value={template.name}
                                    disabled={viewMode}
                                    helperText={error.name}
                                    error={error.name?.length > 0}
                                    onChange={handleTemplateNameChange}
                                    className={classes.formElement}
                                />

                                <FormControl
                                    variant="outlined"
                                    disabled={viewMode}
                                    className={classes.formElement}>
                                    <InputLabel htmlFor="deviceType">
                                        Device Type
                                    </InputLabel>
                                    <CssSelect
                                        fullWidth
                                        name="deviceType"
                                        value={template.deviceType.deviceType}
                                        label="Device Type"
                                        onChange={handleDeviceTypeChange}
                                        disabled={viewMode}
                                        id="deviceType">
                                        {deviceTypes != null &&
                                            deviceTypes.map((data: DeviceType) => (
                                                <MenuItem
                                                    value={data.deviceType}
                                                    key={data.id}>
                                                    {data.deviceType}(
                                                    <>
                                                        {data.width} x {data.height}
                                                    </>
                                                    )
                                                </MenuItem>
                                            ))}
                                    </CssSelect>
                                    <FormHelperText></FormHelperText>
                                </FormControl>

                                {/* <Grid
                                    xs={12}
                                    item
                                    container
                                    className={classes.formElement}>
                                    <Grid
                                        item
                                        container
                                        xs={4}
                                        justify="center"
                                        alignItems="center">
                                        <Typography>Resolution:</Typography>
                                    </Grid>
                                    <Grid item container xs={4}>
                                        <TextField
                                            label="Width"
                                            name="rwidth"
                                            variant="outlined"
                                            required
                                            autoComplete="off"
                                            value={template.deviceType.width}
                                            disabled={true}
                                            onChange={(event: any) => {
                                                const width: any = parseInt(
                                                    event.target.value,
                                                );
                                                const updateTemplate: Template = {
                                                    ...template,
                                                };
                                                updateTemplate.deviceType.width = width;
                                                setTemplate(updateTemplate);
                                            }}
                                        />
                                    </Grid>
                                    <Grid item container xs={4}>
                                        <TextField
                                            label="Height"
                                            name="rheight"
                                            variant="outlined"
                                            required
                                            autoComplete="off"
                                            value={template.deviceType.height}
                                            disabled={true}
                                            onChange={(event: any) => {
                                                const height: any = parseInt(
                                                    event.target.value,
                                                );
                                                const updateTemplate: Template = {
                                                    ...template,
                                                };
                                                updateTemplate.deviceType.height = height;
                                                setTemplate(updateTemplate);
                                            }}
                                        />
                                    </Grid>
                                </Grid> */}
                            </Grid>
                        </Grid>
                    </Grid>
                    {error.assets && error.assets.length > 0 && (
                        <Typography style={{ marginLeft: '20px', color: 'red' }}>
                            {error.assets}
                        </Typography>
                    )}
                    {template.deviceType.width > 0 && (
                        <Grid
                            container
                            xs={4}
                            direction="row"
                            className={classes.addContainerGrid}>
                            <Grid xs item container direction="column">
                                <ButtonBase
                                    name="addVideo"
                                    disabled={viewMode}
                                    className={classes.addVideoButton}
                                    onClick={() => addAssetHandler('video')}></ButtonBase>
                                <Typography className={classes.btnTextSize}>
                                    Add Video
                                </Typography>
                            </Grid>
                            <Grid xs item container direction="column">
                                <ButtonBase
                                    name="addImage"
                                    disabled={viewMode}
                                    className={classes.addImageButton}
                                    onClick={() => addAssetHandler('image')}></ButtonBase>
                                <Typography className={classes.btnTextSize}>
                                    Add Image
                                </Typography>
                            </Grid>
                            <Grid xs item container direction="column">
                                <ButtonBase
                                    name="addDate"
                                    disabled={viewMode}
                                    className={classes.addDateTimeButton}
                                    onClick={() =>
                                        addAssetHandler('datetime')
                                    }></ButtonBase>
                                <Typography className={classes.btnTextSize}>
                                    Add Date
                                </Typography>
                            </Grid>
                            {/* <Grid xs item container direction="column">
                            <ButtonBase
                                name="addWeather"
                                disabled={viewMode}
                                className={classes.addWeatherButton}
                                onClick={() => addAssetHandler('weather')}></ButtonBase>
                            <Typography className={classes.btnTextSize}>
                                Add Weather
                            </Typography>
                        </Grid> */}
                            <Grid xs item container direction="column">
                                <ButtonBase
                                    name="addMultipleImages"
                                    disabled={viewMode}
                                    className={classes.addMultipleImageButton}
                                    onClick={() =>
                                        addAssetHandler('multipleImages')
                                    }></ButtonBase>
                                <Typography className={classes.btnTextSize}>
                                    Add Multiple Images
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                    <Grid container xs={12}>
                        <Grid item xs={9} className={classes.gridElement}>
                            <div
                                style={resolutionStyle}
                                className={classes.divStyle}
                                ref={divRef}>
                                {template.assets.map(
                                    (widget: BoxLayoutProperties, index) =>
                                        widget.action && widget.action !== 'DELETE' ? (
                                            <Rnd
                                                style={{
                                                    ...getStyle(widget),
                                                    zIndex: widget.zIndex,
                                                }}
                                                bounds="parent"
                                                size={{
                                                    width: widget.width,
                                                    height: widget.height,
                                                }}
                                                position={{
                                                    x: widget.left,
                                                    y: widget.top,
                                                }}
                                                onDragStart={(event, dragData) =>
                                                    handleDragStart(index)
                                                }
                                                onDrag={(event, dragData) =>
                                                    handleDrag(event, dragData, index)
                                                }
                                                onDragStop={(event, dragData) =>
                                                    handleDrag(event, dragData, index)
                                                }
                                                key={index}
                                                onResize={(
                                                    e,
                                                    direction,
                                                    ref,
                                                    delta,
                                                    position,
                                                ) => {
                                                    handleResize(ref, position, index);
                                                }}
                                                onResizeStop={(
                                                    e,
                                                    direction,
                                                    ref,
                                                    delta,
                                                    position,
                                                ) => {
                                                    handleResize(ref, position, index);
                                                }}>
                                                <div style={{ margin: '4px' }}>
                                                    <InnerWidgetComponent
                                                        widget={widget}
                                                    />
                                                </div>
                                            </Rnd>
                                        ) : (
                                            <></>
                                        ),
                                )}
                            </div>
                        </Grid>
                        {template.deviceType.width > 0 && (
                            <Grid container item xs={3}>
                                <Grid item className={classes.boxPropertiesParent}>
                                    <Typography align="center">Actions</Typography>
                                    <IconButton
                                        aria-label="Delete"
                                        disabled={viewMode}
                                        onClick={deleteAssetHandler}
                                        size="large">
                                        <DeleteIcon />
                                    </IconButton>

                                    <IconButton
                                        aria-label="Send Backward"
                                        disabled={viewMode}
                                        onClick={sendBackWard}
                                        size="large">
                                        <FlipToBackIcon />
                                    </IconButton>

                                    <IconButton
                                        aria-label="Send Forward"
                                        disabled={viewMode}
                                        onClick={sendForward}
                                        size="large">
                                        <FlipToFrontIcon />
                                    </IconButton>

                                    <Typography align="center">Properties</Typography>
                                    <TextField
                                        label="Top"
                                        name="top"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        autoComplete="off"
                                        disabled={viewMode}
                                        value={
                                            boxLayoutProperties.top * multiplicationFactor
                                        }
                                        onChange={handleBoxLayoutProperties}
                                        className={classes.boxProperties}
                                    />

                                    <TextField
                                        label="Left"
                                        name="left"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        autoComplete="off"
                                        disabled={viewMode}
                                        value={
                                            boxLayoutProperties.left *
                                            multiplicationFactor
                                        }
                                        onChange={handleBoxLayoutProperties}
                                        className={classes.boxProperties}
                                    />

                                    <TextField
                                        label="Width"
                                        name="width"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        autoComplete="off"
                                        disabled={viewMode}
                                        value={
                                            boxLayoutProperties.width *
                                            multiplicationFactor
                                        }
                                        onChange={handleBoxLayoutProperties}
                                        className={classes.boxProperties}
                                    />

                                    <TextField
                                        label="Height"
                                        name="height"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        autoComplete="off"
                                        disabled={viewMode}
                                        value={
                                            boxLayoutProperties.height *
                                            multiplicationFactor
                                        }
                                        onChange={handleBoxLayoutProperties}
                                        className={classes.boxProperties}
                                    />
                                    {dateSelect || weatherSelect ? (
                                        <>
                                            <>
                                                {dateSelect ? (
                                                    <FormControl
                                                        variant="outlined"
                                                        fullWidth
                                                        className={classes.boxProperties}>
                                                        <InputLabel id="demo-simple-select-outlined-label">
                                                            Date Format
                                                        </InputLabel>
                                                        <CssSelect
                                                            labelId="demo-simple-select-outlined-label"
                                                            id="demo-simple-select-outlined"
                                                            fullWidth
                                                            name="dateValue"
                                                            value={
                                                                boxLayoutProperties.dateStyle
                                                            }
                                                            onChange={
                                                                handleBoxLayoutProperties
                                                            }
                                                            label="Date Format">
                                                            {dateValues.map(
                                                                (dateValue: any) => (
                                                                    <MenuItem
                                                                        key={dateValue}
                                                                        value={dateValue}>
                                                                        {dateValue}
                                                                    </MenuItem>
                                                                ),
                                                            )}
                                                        </CssSelect>
                                                    </FormControl>
                                                ) : null}
                                                <FormControl
                                                    variant="outlined"
                                                    fullWidth
                                                    className={classes.boxProperties}>
                                                    <InputLabel id="demo-simple-select-outlined-label">
                                                        Text Size
                                                    </InputLabel>
                                                    <CssSelect
                                                        labelId="demo-simple-select-outlined-label"
                                                        id="demo-simple-select-outlined"
                                                        name="fontSize"
                                                        fullWidth
                                                        value={
                                                            boxLayoutProperties.fontSize
                                                        }
                                                        onChange={
                                                            handleBoxLayoutProperties
                                                        }
                                                        label="Text Size">
                                                        {constantTextSize.map(
                                                            (textSize: any) => (
                                                                <MenuItem
                                                                    key={textSize}
                                                                    value={textSize}>
                                                                    {textSize}
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </CssSelect>
                                                </FormControl>
                                                <>
                                                    <div style={{ display: 'flex' }}>
                                                        <>
                                                            <div
                                                                style={{
                                                                    margin: 'auto 10px',
                                                                }}>
                                                                Bg color:
                                                            </div>
                                                            <FormControl
                                                                className={
                                                                    classes.boxProperties
                                                                }
                                                                variant="outlined"
                                                                style={{ width: '50px' }}>
                                                                <TextField
                                                                    id="outlined-basic"
                                                                    variant="outlined"
                                                                    type="color"
                                                                    name="backgroundColor"
                                                                    value={
                                                                        boxLayoutProperties.backgroundColor
                                                                    }
                                                                    onChange={
                                                                        handleBoxLayoutProperties
                                                                    }
                                                                />
                                                            </FormControl>
                                                        </>
                                                        <>
                                                            <div
                                                                style={{
                                                                    margin: 'auto 10px',
                                                                }}>
                                                                Font color:
                                                            </div>
                                                            <FormControl
                                                                variant="outlined"
                                                                className={
                                                                    classes.boxProperties
                                                                }
                                                                style={{ width: '50px' }}>
                                                                <TextField
                                                                    id="outlined-basic"
                                                                    variant="outlined"
                                                                    type="color"
                                                                    name="fontColor"
                                                                    value={
                                                                        boxLayoutProperties.fontColor
                                                                    }
                                                                    onChange={
                                                                        handleBoxLayoutProperties
                                                                    }
                                                                />
                                                            </FormControl>
                                                        </>
                                                    </div>
                                                </>
                                            </>
                                            {weatherSelect ? (
                                                <>
                                                    <FormControlLabel
                                                        className={classes.boxProperties}
                                                        control={
                                                            <Checkbox
                                                                checked={
                                                                    boxLayoutProperties.weatherText
                                                                }
                                                                onChange={
                                                                    handleBoxLayoutProperties
                                                                }
                                                                name="weatherText"
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Weather"
                                                    />
                                                    <FormControlLabel
                                                        className={classes.boxProperties}
                                                        control={
                                                            <Checkbox
                                                                checked={
                                                                    boxLayoutProperties.locationText
                                                                }
                                                                onChange={
                                                                    handleBoxLayoutProperties
                                                                }
                                                                name="locationText"
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Location"
                                                    />
                                                </>
                                            ) : null}
                                        </>
                                    ) : null}
                                    {multipleImageSelect ? (
                                        <TextField
                                            label="Number Of Images"
                                            name="multipleImages"
                                            variant="outlined"
                                            type="number"
                                            inputProps={{ maxLength: 10 }}
                                            required
                                            fullWidth
                                            autoComplete="off"
                                            disabled={viewMode}
                                            value={boxLayoutProperties.multipleImageCount}
                                            onChange={handleBoxLayoutProperties}
                                            className={classes.boxProperties}
                                        />
                                    ) : null}
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={boxLayoutProperties.isPrimary}
                                                onChange={handleBoxLayoutProperties}
                                                name="isPrimary"
                                               style={{color:"#042630"}}
                                                disabled={viewMode}
                                            />
                                        }
                                        label="Primary"
                                    />

                                    <Grid
                                        item
                                        container
                                        xs={12}
                                        direction="column"
                                        justifyContent="center"
                                        className={classes.submitContainer}>
                                        <Button
                                            disabled={viewMode}
                                            variant="contained"
                                            style={{backgroundColor:"#042630"}}
                                            onClick={_handleSubmit}>
                                            Submit
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </BaseLayout>
            </TemplateErrorBoundry>
        </Root>
    );
};

export default TemplateForm;

interface Props {
	widget: BoxLayoutProperties;
}

const InnerWidgetComponent = (props: Props) => {
	const { widget } = props;
	const weatherText = 'Weather';
	const locationText = 'Location';
	switch (widget.type) {
		case 'weather':
			return (
				<Grid container>
					{widget.weatherText ? (
						<Grid item xs={6}>
							<ul
								className="list"
								style={{ listStyle: 'none', padding: '0px' }}>
								<li>
									&#x26C5;
									{weatherText}
								</li>
							</ul>
						</Grid>
					) : null}
					{widget.locationText ? (
						<Grid item xs={6}>
							<ul
								className="list"
								style={{ listStyle: 'none', padding: '0px' }}>
								<li>
									&#128204;
									{locationText}
								</li>
							</ul>
						</Grid>
					) : null}
				</Grid>
			);

		case 'datetime':
			return <div>{widget.innerText}</div>;
		default:
			return <></>;
	}
};
